import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { monitorEndpoints } from '@/lib/api/monitors';
import type {
  DirectoryMonitor,
  CreateMonitorRequest,
  UpdateMonitorRequest,
  TriggerScanRequest,
  ToggleMonitorRequest,
  MonitorListFilters,
  PaginationParams,
} from '@/features/monitors/types';

/**
 * Query keys for monitor-related queries
 * Centralized key factory for consistent cache management
 */
export const monitorKeys = {
  all: ['monitors'] as const,
  lists: () => [...monitorKeys.all, 'list'] as const,
  list: (params?: MonitorListFilters & PaginationParams) =>
    [...monitorKeys.lists(), params] as const,
  details: () => [...monitorKeys.all, 'detail'] as const,
  detail: (id: string) => [...monitorKeys.details(), id] as const,
  stats: (id: string) => [...monitorKeys.detail(id), 'stats'] as const,
  scans: (id: string, params?: { limit?: number }) =>
    [...monitorKeys.detail(id), 'scans', params] as const,
};

/**
 * Hook to fetch paginated list of directory monitors
 *
 * Provides automatic caching, background refetching, and loading states
 * for monitor list data with optional filtering and pagination.
 *
 * @param params - Optional query parameters
 * @param params.status - Filter by monitor status (active, paused, disabled, error)
 * @param params.dataset_id - Filter by target dataset ID
 * @param params.monitor_type - Filter by monitor type (scheduled, continuous, event-driven)
 * @param params.auto_ingest - Filter by auto-ingest flag
 * @param params.search - Search by name or directory path
 * @param params.limit - Number of items per page
 * @param params.offset - Number of items to skip
 * @param params.sort_by - Field to sort by
 * @param params.sort_order - Sort direction (asc or desc)
 *
 * @example
 * ```tsx
 * function MonitorList() {
 *   const { data, isLoading, error } = useMonitors({
 *     status: 'active',
 *     limit: 20,
 *     sort_by: 'last_scan_at',
 *     sort_order: 'desc'
 *   });
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorAlert error={error} />;
 *
 *   return (
 *     <div>
 *       {data.items.map(monitor => (
 *         <MonitorCard key={monitor.id} monitor={monitor} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useMonitors = (params?: MonitorListFilters & PaginationParams) => {
  return useQuery({
    queryKey: monitorKeys.list(params),
    queryFn: async () => {
      const response = await monitorEndpoints.list(params);
      return response.data;
    },
    staleTime: 300000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to fetch detailed information for a single directory monitor
 *
 * Automatically fetches monitor details including file patterns, target dataset,
 * and associated schema information.
 *
 * @param id - Monitor UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if id provided)
 *
 * @example
 * ```tsx
 * function MonitorDetails({ monitorId }: { monitorId: string }) {
 *   const { data: monitor, isLoading } = useMonitor(monitorId);
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return (
 *     <div>
 *       <h1>{monitor.name}</h1>
 *       <p>Path: {monitor.directory_path}</p>
 *       <FilePatternDisplay patterns={monitor.file_patterns} />
 *       <DatasetInfo dataset={monitor.dataset} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useMonitor = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: monitorKeys.detail(id),
    queryFn: async () => {
      const response = await monitorEndpoints.get(id);
      return response.data;
    },
    enabled: options?.enabled !== false && !!id,
    staleTime: 300000, // Consider data fresh for 5 minutes
  });
};

/**
 * Hook to fetch aggregated statistics for a directory monitor
 *
 * Provides comprehensive metrics including file counts, success rates,
 * scan durations, and data volume processed.
 *
 * @param id - Monitor UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if id provided)
 *
 * @example
 * ```tsx
 * function MonitorStatsPanel({ monitorId }: { monitorId: string }) {
 *   const { data: stats, isLoading } = useMonitorStats(monitorId);
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return (
 *     <div>
 *       <StatCard label="Files Detected" value={stats.files_detected} />
 *       <StatCard label="Success Rate" value={`${stats.success_rate}%`} />
 *       <StatCard label="Avg Scan Time" value={`${stats.average_scan_duration}s`} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useMonitorStats = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: monitorKeys.stats(id),
    queryFn: async () => {
      const response = await monitorEndpoints.getStats(id);
      return response.data;
    },
    enabled: options?.enabled !== false && !!id,
    staleTime: 120000, // Consider data fresh for 2 minutes
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to fetch scan history for a directory monitor
 *
 * Returns a list of scan executions with details about files discovered,
 * jobs created, and any errors encountered.
 *
 * @param id - Monitor UUID
 * @param params - Optional query parameters
 * @param params.limit - Maximum number of scans to return (default: 50)
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if id provided)
 *
 * @example
 * ```tsx
 * function ScanHistory({ monitorId }: { monitorId: string }) {
 *   const { data: scans, isLoading } = useMonitorScans(monitorId, { limit: 10 });
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <Table>
 *       {scans.map(scan => (
 *         <ScanRow key={scan.scan_id} scan={scan} />
 *       ))}
 *     </Table>
 *   );
 * }
 * ```
 */
export const useMonitorScans = (
  id: string,
  params?: { limit?: number },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: monitorKeys.scans(id, params),
    queryFn: async () => {
      const response = await monitorEndpoints.getScans(id, params);
      return response.data;
    },
    enabled: options?.enabled !== false && !!id,
    staleTime: 60000, // Consider data fresh for 1 minute
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to create a new directory monitor
 *
 * Provides optimistic updates and automatic cache invalidation.
 * On success, invalidates monitor list queries to show the new monitor.
 *
 * @example
 * ```tsx
 * function CreateMonitorForm() {
 *   const createMonitor = useCreateMonitor();
 *
 *   const handleSubmit = async (data: CreateMonitorRequest) => {
 *     try {
 *       const response = await createMonitor.mutateAsync(data);
 *       toast.success(`Monitor "${response.monitor.name}" created successfully`);
 *       navigate(`/monitors/${response.monitor.id}`);
 *     } catch (error) {
 *       toast.error('Failed to create monitor');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Input name="name" label="Monitor Name" required />
 *       <Input name="directory_path" label="Directory Path" required />
 *       <FilePatternEditor />
 *       <Button loading={createMonitor.isPending}>Create Monitor</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export const useCreateMonitor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMonitorRequest) => {
      const response = await monitorEndpoints.create(data);
      return response.data;
    },
    onSuccess: (response) => {
      const newMonitor = response.monitor;

      // Invalidate all list queries to fetch updated data
      queryClient.invalidateQueries({ queryKey: monitorKeys.lists() });

      // Optimistically add the new monitor to cache
      queryClient.setQueryData<DirectoryMonitor>(
        monitorKeys.detail(newMonitor.id),
        newMonitor
      );
    },
  });
};

/**
 * Hook to update an existing directory monitor
 *
 * Provides optimistic updates for immediate UI feedback.
 * Automatically invalidates related queries on success.
 *
 * @example
 * ```tsx
 * function EditMonitorForm({ monitor }: { monitor: DirectoryMonitor }) {
 *   const updateMonitor = useUpdateMonitor();
 *
 *   const handleSubmit = async (data: UpdateMonitorRequest) => {
 *     try {
 *       await updateMonitor.mutateAsync({ id: monitor.id, data });
 *       toast.success('Monitor updated successfully');
 *     } catch (error) {
 *       toast.error('Failed to update monitor');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Input name="description" defaultValue={monitor.description} />
 *       <Select name="scan_frequency" defaultValue={monitor.scan_frequency} />
 *       <Button loading={updateMonitor.isPending}>Save Changes</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export const useUpdateMonitor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateMonitorRequest;
    }) => {
      const response = await monitorEndpoints.update(id, data);
      return response.data;
    },
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: monitorKeys.detail(id) });

      // Snapshot previous value for rollback
      const previousMonitor = queryClient.getQueryData<DirectoryMonitor>(
        monitorKeys.detail(id)
      );

      // Optimistically update cache
      if (previousMonitor) {
        queryClient.setQueryData<DirectoryMonitor>(monitorKeys.detail(id), {
          ...previousMonitor,
          ...data,
          updated_at: new Date().toISOString(),
        });
      }

      return { previousMonitor };
    },
    onError: (_error, { id }, context) => {
      // Rollback on error
      if (context?.previousMonitor) {
        queryClient.setQueryData(monitorKeys.detail(id), context.previousMonitor);
      }
    },
    onSuccess: (response, { id }) => {
      const updatedMonitor = response.monitor;

      // Update detail cache with server response
      queryClient.setQueryData(monitorKeys.detail(id), updatedMonitor);

      // Invalidate list queries to reflect changes
      queryClient.invalidateQueries({ queryKey: monitorKeys.lists() });

      // Invalidate stats if they might have changed
      queryClient.invalidateQueries({ queryKey: monitorKeys.stats(id) });
    },
  });
};

/**
 * Hook to delete a directory monitor
 *
 * Removes monitor from cache on success and invalidates list queries.
 * WARNING: This will stop monitoring the directory and remove scan history.
 * Active scans will be cancelled. This operation cannot be undone.
 *
 * @example
 * ```tsx
 * function DeleteMonitorButton({ monitorId }: { monitorId: string }) {
 *   const deleteMonitor = useDeleteMonitor();
 *
 *   const handleDelete = async () => {
 *     if (!confirm('Are you sure? This will stop monitoring and cannot be undone.')) return;
 *
 *     try {
 *       await deleteMonitor.mutateAsync(monitorId);
 *       toast.success('Monitor deleted successfully');
 *       navigate('/monitors');
 *     } catch (error) {
 *       toast.error('Failed to delete monitor. It may have active scans.');
 *     }
 *   };
 *
 *   return (
 *     <Button variant="destructive" onClick={handleDelete} loading={deleteMonitor.isPending}>
 *       Delete Monitor
 *     </Button>
 *   );
 * }
 * ```
 */
export const useDeleteMonitor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await monitorEndpoints.delete(id);
      return id;
    },
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: monitorKeys.detail(id) });

      // Snapshot previous value for rollback
      const previousMonitor = queryClient.getQueryData<DirectoryMonitor>(
        monitorKeys.detail(id)
      );

      // Optimistically remove from cache
      queryClient.removeQueries({ queryKey: monitorKeys.detail(id) });

      return { previousMonitor };
    },
    onError: (_error, id, context) => {
      // Rollback on error
      if (context?.previousMonitor) {
        queryClient.setQueryData(monitorKeys.detail(id), context.previousMonitor);
      }
    },
    onSuccess: (deletedId) => {
      // Remove all related queries
      queryClient.removeQueries({ queryKey: monitorKeys.detail(deletedId) });
      queryClient.removeQueries({ queryKey: monitorKeys.stats(deletedId) });
      queryClient.removeQueries({
        queryKey: [...monitorKeys.detail(deletedId), 'scans'],
      });

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: monitorKeys.lists() });
    },
  });
};

/**
 * Hook to trigger a manual directory scan
 *
 * Initiates an immediate scan of the monitored directory, bypassing
 * the regular schedule. Use the force flag to scan even if recently scanned.
 *
 * WARNING: This is a potentially long-running operation (up to 60 seconds).
 * The request will return immediately with a scan_id for tracking.
 *
 * @example
 * ```tsx
 * function TriggerScanButton({ monitorId }: { monitorId: string }) {
 *   const triggerScan = useTriggerScan();
 *
 *   const handleTrigger = async () => {
 *     try {
 *       const response = await triggerScan.mutateAsync({
 *         monitor_id: monitorId,
 *         force: true
 *       });
 *       toast.success(`Scan triggered: ${response.scan_id}`);
 *     } catch (error) {
 *       toast.error('Failed to trigger scan. Monitor may be paused.');
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleTrigger} loading={triggerScan.isPending}>
 *       Trigger Scan
 *     </Button>
 *   );
 * }
 * ```
 */
export const useTriggerScan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: TriggerScanRequest) => {
      const response = await monitorEndpoints.triggerScan(
        request.monitor_id,
        request
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      const monitorId = variables.monitor_id;

      // Invalidate stats and scans to show the new scan
      queryClient.invalidateQueries({ queryKey: monitorKeys.stats(monitorId) });
      queryClient.invalidateQueries({
        queryKey: [...monitorKeys.detail(monitorId), 'scans'],
      });

      // Update the monitor's last_scan_at timestamp optimistically
      const existingMonitor = queryClient.getQueryData<DirectoryMonitor>(
        monitorKeys.detail(monitorId)
      );
      if (existingMonitor) {
        queryClient.setQueryData<DirectoryMonitor>(
          monitorKeys.detail(monitorId),
          {
            ...existingMonitor,
            last_scan_at: new Date().toISOString(),
          }
        );
      }
    },
  });
};

/**
 * Hook to toggle monitor status between active and paused
 *
 * Use this to temporarily pause a monitor without deleting it.
 * Paused monitors will not execute scheduled scans but retain all configuration.
 *
 * Provides optimistic updates for immediate UI feedback.
 *
 * @example
 * ```tsx
 * function MonitorStatusToggle({ monitor }: { monitor: DirectoryMonitor }) {
 *   const toggleStatus = useToggleMonitorStatus();
 *   const isActive = monitor.status === 'active';
 *
 *   const handleToggle = async () => {
 *     try {
 *       await toggleStatus.mutateAsync({
 *         monitor_id: monitor.id,
 *         status: isActive ? 'paused' : 'active'
 *       });
 *       toast.success(`Monitor ${isActive ? 'paused' : 'resumed'}`);
 *     } catch (error) {
 *       toast.error('Failed to update monitor status');
 *     }
 *   };
 *
 *   return (
 *     <Switch
 *       checked={isActive}
 *       onCheckedChange={handleToggle}
 *       disabled={toggleStatus.isPending}
 *     />
 *   );
 * }
 * ```
 */
export const useToggleMonitorStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ToggleMonitorRequest) => {
      const response = await monitorEndpoints.toggleStatus(
        request.monitor_id,
        request
      );
      return response.data;
    },
    onMutate: async (request) => {
      const monitorId = request.monitor_id;
      const newStatus = request.status;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: monitorKeys.detail(monitorId) });

      // Snapshot previous value for rollback
      const previousMonitor = queryClient.getQueryData<DirectoryMonitor>(
        monitorKeys.detail(monitorId)
      );

      // Optimistically update cache
      if (previousMonitor) {
        queryClient.setQueryData<DirectoryMonitor>(
          monitorKeys.detail(monitorId),
          {
            ...previousMonitor,
            status: newStatus,
            updated_at: new Date().toISOString(),
          }
        );
      }

      return { previousMonitor };
    },
    onError: (_error, request, context) => {
      // Rollback on error
      if (context?.previousMonitor) {
        queryClient.setQueryData(
          monitorKeys.detail(request.monitor_id),
          context.previousMonitor
        );
      }
    },
    onSuccess: (response, request) => {
      const monitorId = request.monitor_id;
      const updatedMonitor = response.monitor;

      // Update detail cache with server response
      queryClient.setQueryData(monitorKeys.detail(monitorId), updatedMonitor);

      // Invalidate list queries to reflect status change
      queryClient.invalidateQueries({ queryKey: monitorKeys.lists() });
    },
  });
};

/**
 * Hook to validate a directory path before creating a monitor
 *
 * Checks if a directory path exists, is accessible, and provides metadata
 * about the directory contents. Use this before creating a monitor.
 *
 * Does not cache results since validation is path-specific and ephemeral.
 *
 * @example
 * ```tsx
 * function DirectoryPathInput() {
 *   const validateDirectory = useValidateDirectory();
 *   const [path, setPath] = useState('');
 *
 *   const handleValidate = async () => {
 *     try {
 *       const result = await validateDirectory.mutateAsync(path);
 *       if (!result.valid) {
 *         toast.error(`Invalid path: ${result.errors[0]?.message}`);
 *       } else {
 *         toast.success(
 *           `Valid path with ${result.metadata?.matching_files} files`
 *         );
 *       }
 *     } catch (error) {
 *       toast.error('Failed to validate directory path');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <Input
 *         value={path}
 *         onChange={(e) => setPath(e.target.value)}
 *         placeholder="/data/incoming"
 *       />
 *       <Button onClick={handleValidate} loading={validateDirectory.isPending}>
 *         Validate Path
 *       </Button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useValidateDirectory = () => {
  return useMutation({
    mutationFn: async (path: string) => {
      const response = await monitorEndpoints.validateDirectory(path);
      return response.data;
    },
    // No cache management needed - validation results are ephemeral
  });
};

/**
 * Hook to list subdirectories in a directory path
 *
 * Returns a list of immediate subdirectories with optional metadata
 * like file counts and sizes. Used for partition mapping in monitor creation.
 *
 * @example
 * ```tsx
 * function SubdirectoryList({ directoryPath }: { directoryPath: string }) {
 *   const listSubdirectories = useListSubdirectories();
 *   const [subdirs, setSubdirs] = useState<SubdirectoryInfo[]>([]);
 *
 *   useEffect(() => {
 *     if (directoryPath) {
 *       listSubdirectories.mutateAsync(directoryPath)
 *         .then(result => setSubdirs(result.subdirectories))
 *         .catch(err => console.error('Failed to list subdirectories:', err));
 *     }
 *   }, [directoryPath]);
 *
 *   return (
 *     <ul>
 *       {subdirs.map(subdir => (
 *         <li key={subdir.name}>{subdir.name}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export const useListSubdirectories = () => {
  return useMutation({
    mutationFn: async ({ path, depth = 1 }: { path: string; depth?: number }) => {
      const response = await monitorEndpoints.listSubdirectories(path, depth);
      return response.data;
    },
    // No cache management needed - subdirectory listing is ephemeral
  });
};
